import styled from "styled-components";
import { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useInfiniteQuery } from "@tanstack/react-query";

import {
  PageHeading,
  SectionHeading,
  SrOnly,
  TeamHeading,
} from "@/components/Layout/Parts";
import ExportButton from "@/components/ExportButton";
import Button from "@/components/Button";
import { ReactComponent as PlusIcon } from "@/assets/icons/plus.svg";
import FilterButton from "@/components/FilterDropdown/FilterButton";
import { FilterWrap } from "@/components/FilterDropdown/Parts";
import FiltersDropdown from "@/components/FilterDropdown";
import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";
import FilterMultiselect from "@/components/FilterDropdown/FilterMultiselect";
import ScrollableTable from "@/components/ScrollableTable";
import { SortableTh, Table, TableContent, Th } from "@/components/Table";
import TipRow from "./TipRow";
import {
  PaginatedResponse,
  SortState,
  VideoTipResource,
} from "@/types/resources";
import { SortDirection } from "@/utils/enums";
import { stringsArrayToDropdownOptions } from "@/utils/helpers";
import { categories, videoTypes } from "@/utils/data/tips";
import TipForm from "./TipForm";
import FullScreenButton from "@/components/FullScreenButton";
import { api } from "@/utils/api";
import InfiniteLoader from "@/components/InfiniteLoader";

const TopButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
`;

const StyledFilterButton = styled(FilterButton)`
  margin: 0;
`;

const StyledFiltersDropdown = styled(FiltersDropdown)`
  width: 320px;
`;

const StyledFullScreenButton = styled(FullScreenButton)`
  margin: 0;
`;

const StyledTable = styled(Table)`
  th,
  td {
    &:first-child {
      padding-left: 24px;
      width: 88px;
    }
    &:nth-child(2) {
      width: 190px;
    }
    &:nth-child(3) {
      width: 172px;
    }
    &:nth-child(4) {
      width: 124px;
    }
    &:nth-child(5) {
      width: 172px;
    }
    &:nth-child(6) {
      width: 102px;
    }
    &:nth-child(7) {
      width: 110px;
    }
    &:nth-child(8) {
      width: 120px;
    }
    &:nth-child(9) {
      width: 100px;
    }
    &:last-child {
      padding-right: 24px;
      width: 64px;
    }
  }
`;

export const typeOptions = stringsArrayToDropdownOptions(videoTypes);

export const categoryOptions = stringsArrayToDropdownOptions(categories);

const DistributionTipsLibrary = () => {
  const [showFilters, setShowFilters] = useState(false);

  const [showCreationForm, setShowCreationForm] = useState(false);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  const [selectedTypes, setSelectTypes] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [sort, setSort] = useState<SortState>({
    direction: SortDirection.DEFAULT,
    property: "id",
  });

  const params = {
    ...(sort.direction !== SortDirection.DEFAULT && {
      sort: `${sort.direction === SortDirection.DESC ? "-" : ""}${
        sort.property
      }`,
    }),
    ...(!!debouncedSearch && {
      "filter[search]": debouncedSearch,
    }),
    ...(!!selectedTypes.length && {
      "filter[video_type]": selectedTypes.join(","),
    }),
    ...(!!selectedCategories.length && {
      "filter[category]": selectedCategories.join(","),
    }),
  };

  const fetchTips = ({ pageParam = "/v1/video-tips" }) =>
    api.get<never, PaginatedResponse<VideoTipResource>>(pageParam, {
      params,
    });

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<VideoTipResource>>(
    ["video_tips", params],
    fetchTips,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 120 * 1000,
    }
  );

  const tips = useMemo(
    () =>
      data?.pages?.reduce<VideoTipResource[]>((acc, curr) => {
        return [...acc, ...curr.data];
      }, []),
    [data]
  );

  const fetchCsv = () =>
    api.get<never, string>("/v1/video-tips/export", {
      params,
    });

  const isFiltersApplied = !!(
    selectedTypes.length || selectedCategories.length
  );

  return (
    <>
      <TeamHeading>All Teams</TeamHeading>
      <PageHeading>Tips Library</PageHeading>
      <TopButtons>
        <ExportButton fileName="tips-library.csv" fetchCsv={fetchCsv} />
        <Button color="action" small onClick={() => setShowCreationForm(true)}>
          <PlusIcon />
          New tip
        </Button>
        <AnimatePresence>
          {showCreationForm && (
            <TipForm onClose={() => setShowCreationForm(false)} />
          )}
        </AnimatePresence>
        <FilterWrap>
          <StyledFilterButton
            isActive={showFilters}
            onClick={(e) => {
              e.stopPropagation();
              setShowFilters((prev) => !prev);
            }}
            filterApplied={isFiltersApplied}
          />
          {showFilters && (
            <StyledFiltersDropdown onClose={() => setShowFilters(false)}>
              <FilterMultiselect<string>
                label="By Type:"
                values={selectedTypes}
                setValues={setSelectTypes}
                options={typeOptions}
              />
              <FilterMultiselect<string>
                label="By Category:"
                values={selectedCategories}
                setValues={setSelectedCategories}
                options={categoryOptions}
              />
            </StyledFiltersDropdown>
          )}
        </FilterWrap>
        <StyledFullScreenButton />
      </TopButtons>
      <SearchBar
        value={search}
        setValue={setSearch}
        placeholder="Search by Name, ID"
      />
      <SectionHeading>All tips</SectionHeading>
      <ScrollableTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Tip ID</Th>
              <Th>Name</Th>
              <Th>Thumbnail</Th>
              <SortableTh
                title="Duration"
                property="duration"
                sort={sort}
                setSort={setSort}
              />
              <Th>Embed Link</Th>
              <SortableTh
                title="Type"
                property="video_type"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Category"
                property="category"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Subcategory"
                property="subcategory"
                sort={sort}
                setSort={setSort}
              />
              <SortableTh
                title="Mashup"
                property="mashup"
                sort={sort}
                setSort={setSort}
              />
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!tips?.length}
            >
              {tips?.map((tip) => (
                <TipRow tip={tip} key={tip.id} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default DistributionTipsLibrary;
